import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useState } from "react";
import InstantTenantScore from "components/InstantTenantScore";
import QuickScoreMobile from "components/InstantTenantScore/QuickScoreMobile";

// Dummy application data
const applications = [
  {
    id: "APP-8724",
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    phone: "(555) 123-4567",
    property: "Sunset Apartments #304",
    rent: "$1,850/mo",
    date: "May 18, 2025",
    status: "Pending Review",
    creditScore: 742,
    income: "$85,000",
    backgroundCheck: "Completed",
    references: 3,
  },
  {
    id: "APP-8712",
    name: "Michael Chen",
    email: "mchen@example.com",
    phone: "(555) 987-6543",
    property: "Highland Residences #201",
    rent: "$2,250/mo",
    date: "May 17, 2025",
    status: "Document Required",
    creditScore: 768,
    income: "$92,000",
    backgroundCheck: "Pending",
    references: 2,
  },
  {
    id: "APP-8698",
    name: "Jessica Parker",
    email: "jparker@example.com",
    phone: "(555) 234-5678",
    property: "Oakwood Terrace #105",
    rent: "$1,695/mo",
    date: "May 16, 2025",
    status: "Under Review",
    creditScore: 712,
    income: "$76,500",
    backgroundCheck: "Completed",
    references: 3,
  },
  {
    id: "APP-8683",
    name: "Robert Wilson",
    email: "r.wilson@example.com",
    phone: "(555) 345-6789",
    property: "Sunset Apartments #417",
    rent: "$1,950/mo",
    date: "May 15, 2025",
    status: "Pending Review",
    creditScore: 705,
    income: "$79,000",
    backgroundCheck: "Completed",
    references: 2,
  },
  {
    id: "APP-8675",
    name: "Aisha Martinez",
    email: "aisha.m@example.com",
    phone: "(555) 456-7890",
    property: "Highland Residences #312",
    rent: "$2,300/mo",
    date: "May 15, 2025",
    status: "Interview Scheduled",
    creditScore: 796,
    income: "$110,000",
    backgroundCheck: "Completed",
    references: 4,
  },
  {
    id: "APP-8664",
    name: "David Thompson",
    email: "dthompson@example.com",
    phone: "(555) 567-8901",
    property: "Oakwood Terrace #210",
    rent: "$1,795/mo",
    date: "May 14, 2025",
    status: "Under Review",
    creditScore: 675,
    income: "$68,000",
    backgroundCheck: "In Progress",
    references: 2,
  },
];

// Status chip color mapping
const getStatusColor = (status) => {
  const statusMap = {
    "Pending Review": "warning",
    "Document Required": "error",
    "Under Review": "info",
    "Interview Scheduled": "success"
  };
  return statusMap[status] || "default";
};

function Applications() {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card sx={{ padding: 3, mb: 3 }}>
          <ArgonTypography variant="h4" fontWeight="medium">
            Tenant Applications
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" mt={1} mb={3}>
            View and manage rental applications submitted by potential tenants.
          </ArgonTypography>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="application tabs"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="All Applications (42)" />
            <Tab label="Pending Review (18)" />
            <Tab label="Document Required (7)" />
            <Tab label="Under Review (12)" />
            <Tab label="Interview Scheduled (5)" />
          </Tabs>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search applications..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button variant="outlined" startIcon={<FilterListIcon />} sx={{ mr: 2 }}>
                Filter
              </Button>
              <Button variant="contained" color="primary">
                New Application
              </Button>
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table sx={{ minWidth: 650 }} aria-label="applications table">
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Property & Rent</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Credit Score</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((application) => (
                  <TableRow key={application.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>{application.name.charAt(0)}</Avatar>
                        <Box>
                          <ArgonTypography variant="subtitle2">
                            {application.name}
                          </ArgonTypography>
                          <ArgonTypography variant="caption" color="text">
                            {application.email} • {application.phone}
                          </ArgonTypography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <ArgonTypography variant="subtitle2">
                        {application.property}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        {application.rent}
                      </ArgonTypography>
                    </TableCell>
                    <TableCell>{application.date}</TableCell>
                    <TableCell>
                      <Chip 
                        label={application.status} 
                        size="small" 
                        color={getStatusColor(application.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <ArgonTypography
                        variant="body2"
                        color={application.creditScore > 720 ? "success.main" : 
                               application.creditScore > 680 ? "warning.main" : "error.main"}
                        fontWeight="medium"
                      >
                        {application.creditScore}
                      </ArgonTypography>
                    </TableCell>
                    <TableCell>
                      <Button variant="outlined" size="small" sx={{ mr: 1 }}>
                        Review
                      </Button>
                      <Button variant="text" size="small" color="info">
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <ArgonTypography variant="body2" color="text">
              Showing 6 of 42 applications
            </ArgonTypography>
            <Box>
              <Button disabled>Previous</Button>
              <Button variant="contained" size="small" sx={{ mx: 1 }}>1</Button>
              <Button variant="text" size="small" sx={{ mx: 1 }}>2</Button>
              <Button variant="text" size="small" sx={{ mx: 1 }}>3</Button>
              <Button>Next</Button>
            </Box>
          </Box>
        </Card>
        
        <Card sx={{ padding: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                Application Statistics
              </ArgonTypography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <ArgonTypography variant="body2" color="text">
                  Total Applications
                </ArgonTypography>
                <ArgonTypography variant="h4" fontWeight="bold">
                  42
                </ArgonTypography>
                <ArgonTypography variant="caption" color="success.main">
                  +12% from last month
                </ArgonTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <ArgonTypography variant="body2" color="text">
                  Approval Rate
                </ArgonTypography>
                <ArgonTypography variant="h4" fontWeight="bold">
                  68%
                </ArgonTypography>
                <ArgonTypography variant="caption" color="success.main">
                  +5% from last month
                </ArgonTypography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <ArgonTypography variant="body2" color="text">
                  Average Processing Time
                </ArgonTypography>
                <ArgonTypography variant="h4" fontWeight="bold">
                  3.2 days
                </ArgonTypography>
                <ArgonTypography variant="caption" color="error.main">
                  +0.5 days from last month
                </ArgonTypography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                Recent Activity
              </ArgonTypography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ borderLeft: 2, borderColor: 'success.main', pl: 2, mb: 2 }}>
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Application Approved
                  </ArgonTypography>
                                      <ArgonTypography variant="caption" color="text">
                    Thomas Lee&apos;s application for Oakwood Terrace #108 was approved 2 hours ago
                  </ArgonTypography>
                </Box>
                <Box sx={{ borderLeft: 2, borderColor: 'warning.main', pl: 2, mb: 2 }}>
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Document Requested
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Requested additional income verification from Michael Chen 4 hours ago
                  </ArgonTypography>
                </Box>
                <Box sx={{ borderLeft: 2, borderColor: 'info.main', pl: 2, mb: 2 }}>
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Interview Scheduled
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Scheduled interview with Aisha Martinez for tomorrow at 2:00 PM
                  </ArgonTypography>
                </Box>
                <Box sx={{ borderLeft: 2, borderColor: 'success.main', pl: 2 }}>
                  <ArgonTypography variant="body2" fontWeight="medium">
                    Application Submitted
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    New application received from Sarah Johnson for Sunset Apartments #304
                  </ArgonTypography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Applications;